/**
 * Crop Insurance Actuarial Pricing Tool
 * Main Application Component
 * 
 * This application provides:
 * - Project management for organizing work
 * - Term sheet upload and AI-powered parsing
 * - Weather data upload, visualization, and processing
 * - Actuarial rate calculations
 * - Results visualization and export
 */

import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SplashLogin from "@/components/auth/SplashLogin";

// Import page components
import LandingPage from "@/pages/LandingPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import NavBar from "@/components/NavBar/NavBar";
import RWBCISOutlet from "./pages/RWBCIOutlet";
import PMFBYOutlet from "./pages/PMFBYOutlet";
import PariChat from "./pages/PariChat"; // Add this import

const RWBCISLanding      = React.lazy(() => import("./pages/RWBCISLanding"));
const RWBCISHowItWorks      = React.lazy(() => import("./pages/RWBCISHowItWorks"));
const UploadPage         = React.lazy(() => import("@/pages/UploadPage"));
const ReviewPage         = React.lazy(() => import("@/pages/ReviewPage"));
const WeatherPage        = React.lazy(() => import("@/pages/WeatherPage"));
const WeatherViewPage    = React.lazy(() => import("@/pages/WeatherViewPage"));
const BurningCostPage    = React.lazy(() => import("@/pages/BurningCostPage"));
const ResultsPage        = React.lazy(() => import("@/pages/ResultsPage"));
// Help Page
const HelpPage           = React.lazy(() => import("@/pages/HelpPage"));
const SettingsPage       = React.lazy(() => import("@/pages/SettingsPage"));
// Payout Module. There is no separate projects/history page: the RWBCIS
// landing IS the payout module's home and already lists payout projects on its
// Payout tab. `pages/PayoutProjectsPage.jsx` was a second listing titled
// "Payout History" that showed PRICING projects alongside them, which is what
// made leaving a payout project land somewhere confusing. `/payout` now
// redirects, so old links and bookmarks still arrive in the right place.
const PayoutOutlet       = React.lazy(() => import("@/pages/PayoutOutlet"));
const PayoutResultsPage  = React.lazy(() => import("@/pages/PayoutResultsPage"));
// PARI Product Builder
const PariLanding        = React.lazy(() => import("@/pages/PariLanding"));
const PariUIBuilder      = React.lazy(() => import("@/pages/PariUIBuilder"));
// PMFBY — Yield-Based Crop Insurance Pricing
const PMFBYLanding        = React.lazy(() => import("@/pages/PMFBYLanding"));
const PmfbyProjectLayout  = React.lazy(() => import("@/pages/pmfby/PmfbyProjectLayout"));
const PmfbyUploadPage     = React.lazy(() => import("@/pages/pmfby/PmfbyUploadPage"));
const PmfbyDataProcessing = React.lazy(() => import("@/pages/pmfby/PmfbyDataProcessingPage"));
const PmfbyDataAnalysis   = React.lazy(() => import("@/pages/pmfby/PmfbyDataAnalysisPage"));
const PmfbyDataPrepPage   = React.lazy(() => import("@/pages/pmfby/PmfbyDataPrepPage"));
const PmfbySettingsPage   = React.lazy(() => import("@/pages/pmfby/PmfbySettingsPage"));
const PmfbyPricingPage    = React.lazy(() => import("@/pages/pmfby/PmfbyPricingPage"));
const PmfbyAssumptions    = React.lazy(() => import("@/pages/pmfby/PmfbyAssumptionsPage"));

const SuspenseLoader = ({ children }) => {
  return <React.Suspense fallback={
    <section className="p-8 bg-background">
      Loading...
    </section>
  }>{children}</React.Suspense>
}

// Gate the whole app behind authentication. Shows the PARI splash → login when
// the user is not signed in.
const AuthGate = ({ children }) => {
  const { user, loading, login } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-slate-400 animate-pulse text-sm tracking-widest font-bold">pycrop…</div>
      </div>
    );
  }
  if (!user) return <SplashLogin onAuthed={login} />;
  return children;
};

function App() {
  return (
    <div className="App bg-background">
      <AuthProvider>
      <BrowserRouter>
        <AuthGate>
        <NavBar />
        <Routes>
          {/* Landing Page - Entry point */}
          <Route path="/" element={<LandingPage />} />

          {/* PARI Product Builder */}
          <Route path="/pycrop" element={
            <SuspenseLoader><PariLanding /></SuspenseLoader>
          } />
          <Route path="/pycrop-ai" element={<PariChat />} />
          <Route path="/pycrop-ui" element={
            <SuspenseLoader><PariUIBuilder /></SuspenseLoader>
          } />

          {/* Projects Management */}
          <Route path="/RWBCIS" element={<RWBCISOutlet />}>
            <Route index element={
              <SuspenseLoader>
                <RWBCISLanding />
              </SuspenseLoader>}
            />
          </Route>

          {/* ── PMFBY — Yield-Based Crop Insurance Pricing ─────────────── */}
          <Route path="/PMFBY" element={<PMFBYOutlet />}>
            <Route index element={<SuspenseLoader><PMFBYLanding /></SuspenseLoader>} />
            <Route path="projects/:projectId" element={
              <SuspenseLoader><PmfbyProjectLayout /></SuspenseLoader>}>
              <Route index element={<Navigate to="upload" />} />
              <Route path="upload" element={<SuspenseLoader><PmfbyUploadPage /></SuspenseLoader>} />
              <Route path="prep" element={<SuspenseLoader><PmfbyDataPrepPage /></SuspenseLoader>} />
              <Route path="process" element={<SuspenseLoader><PmfbyDataProcessing /></SuspenseLoader>} />
              <Route path="analysis" element={<SuspenseLoader><PmfbyDataAnalysis /></SuspenseLoader>} />
              <Route path="settings" element={<SuspenseLoader><PmfbySettingsPage /></SuspenseLoader>} />
              <Route path="pricing" element={<SuspenseLoader><PmfbyPricingPage /></SuspenseLoader>} />
              <Route path="assumptions" element={<SuspenseLoader><PmfbyAssumptions /></SuspenseLoader>} />
            </Route>
          </Route>

          <Route path="projects/:projectId" element={<ProjectDetailPage />}>
            {/* Term Sheet Upload and Parsing */}
            <Route path="" element={
              <Navigate to="upload" />
            } />
            <Route path="upload" element={
              <SuspenseLoader>
                <UploadPage />
              </SuspenseLoader>
            } />

            {/* Review and Edit Parsed Data */}
            <Route path="review/:termSheetId" element={
              <SuspenseLoader>
                <ReviewPage />
              </SuspenseLoader>
            } />

            {/* Weather Data Upload */}
            <Route path="weather/:termSheetId" element={
              <SuspenseLoader>
                <WeatherPage />
              </SuspenseLoader>
            } />

            {/* Burning Cost (historical loss experience) */}
            <Route path="burning-cost/:termSheetId" element={
              <SuspenseLoader>
                <BurningCostPage />
              </SuspenseLoader>
            } />

            {/* Results Dashboard */}
            <Route path="results/:termSheetId" element={
              <SuspenseLoader>
                <ResultsPage />
              </SuspenseLoader>
            } />
          </Route>

          {/* Weather Data Visualization
          <Route path="weather-view/:weatherId" element={
            <SuspenseLoader>
              <WeatherViewPage />
            </SuspenseLoader>
          } /> */}

          {/* Help Page */}
          <Route path="/settings" element={
            <SuspenseLoader><SettingsPage /></SuspenseLoader>
          } />
          <Route path="/help" element={
            <SuspenseLoader><HelpPage /></SuspenseLoader>
          } />

          {/* ── Payout Module ─────────────────────────────────────────── */}
          <Route path="/RWBCIS/how-it-works" element={
            <SuspenseLoader><RWBCISHowItWorks /></SuspenseLoader>
          } />
          <Route path="payout" element={<Navigate to="/RWBCIS?tab=payout" replace />} />
          <Route path="payout/:projectId" element={
            <SuspenseLoader><PayoutOutlet /></SuspenseLoader>
          }>
            <Route path="" element={<Navigate to="upload" />} />
            <Route path="upload" element={
              <SuspenseLoader><UploadPage /></SuspenseLoader>
            } />
            <Route path="review/:termSheetId" element={
              <SuspenseLoader><ReviewPage /></SuspenseLoader>
            } />
            <Route path="weather/:termSheetId" element={
              <SuspenseLoader><WeatherPage /></SuspenseLoader>
            } />
            <Route path="results/:termSheetId" element={
              <SuspenseLoader><PayoutResultsPage /></SuspenseLoader>
            } />
          </Route>

          {/* Catch-all: an unmatched path used to render the NavBar over an
              empty page (a blank screen with no error). Send it home. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthGate>
      </BrowserRouter>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;